import React, { useState, useEffect } from 'react';
import { auth, db } from '../../firebase/config';
import { collection, query, getDocs, doc, getDoc, setDoc, updateDoc, addDoc } from 'firebase/firestore';


import NavBar from '../NavBar/NavBar';
import { appConfig } from '../../config/appConfig';
import './HistorialViajes.css';
import * as XLSX from 'xlsx';

// Las modificaciones y duplicaciones del historial están deshabilitadas.
const HISTORY_ACTIONS_ENABLED = false;

function HistorialViajes() {
  const [viajes, setViajes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState('todos');
  const [expandedViajeId, setExpandedViajeId] = useState(null);

  // Add the missing state variables
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [departamentoFilter, setDepartamentoFilter] = useState('');
  const [ccClienteFilter, setCcClienteFilter] = useState('');
  const [triggerSearch, setTriggerSearch] = useState(0);

  // Estado para la vista (ahora se carga desde perfil del usuario si existe)
  const [vistaTabla, setVistaTabla] = useState(window.innerWidth >= 768);
  const [guardandoPreferencias, setGuardandoPreferencias] = useState(false);

  // New state to store the user's department
  const [userDepartment, setUserDepartment] = useState('');
  // State to control whether to show only user's department trips
  const [filterByUserDept, setFilterByUserDept] = useState(true);
  // State to track if user is admin
  const [isAdmin, setIsAdmin] = useState(false);

  // Add the search trigger function
  const handleSearchClick = () => {
    setTriggerSearch(prev => prev + 1);
  };

  // Add pagination state variables
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Default items per page
  // Add view limiting options
  const [maxViewItems, setMaxViewItems] = useState(50); // Default max items to load

  // Add passenger modal states with ESLint disable comment
  // eslint-disable-next-line no-unused-vars
  const [passengerModalOpen, setPassengerModalOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [selectedPassengers, setSelectedPassengers] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [selectedTripData, setSelectedTripData] = useState(null);

  // Edit trip states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingViaje, setEditingViaje] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);


  // Efecto para cargar preferencias de usuario desde Firestore
  useEffect(() => {
    const cargarPreferenciasUsuario = async () => {
      if (!auth.currentUser) return;

      try {
        const userPrefsRef = doc(db, 'levapan_userPreferences', auth.currentUser.uid);
        const userPrefsDoc = await getDoc(userPrefsRef);

        // Check if user has manually set preference before
        const hasManualPreference = localStorage.getItem('userSetViewPreference') === 'true';

        if (userPrefsDoc.exists() && hasManualPreference) {
          const prefs = userPrefsDoc.data();
          // Only use saved preferences if user manually set them before
          if (prefs.vistaTabla !== undefined) {
            setVistaTabla(prefs.vistaTabla);
          }
          if (prefs.filtroViajes) {
            setFiltro(prefs.filtroViajes);
          }
        } else {
          // Otherwise use automatic detection based on screen size
          setVistaTabla(window.innerWidth >= 768);
        }
      } catch (err) {
        console.error("Error al cargar preferencias:", err);
        // Fallback to automatic behavior
        setVistaTabla(window.innerWidth >= 768);
      }
    };

    cargarPreferenciasUsuario();
  }, []);

  // Efecto para guardar preferencias cuando cambia la vista o filtro
  useEffect(() => {
    const guardarPreferenciasUsuario = async () => {
      if (!auth.currentUser) return;

      try {
        setGuardandoPreferencias(true);
        const userPrefsRef = doc(db, 'levapan_userPreferences', auth.currentUser.uid);

        await setDoc(userPrefsRef, {
          vistaTabla,
          filtroViajes: filtro,
          lastUpdated: new Date()
        }, { merge: true });
      } catch (err) {
        console.error("Error al guardar preferencias:", err);
      } finally {
        setGuardandoPreferencias(false);
      }
    };

    // Solo guardamos si ya se ha inicializado (no durante la carga inicial)
    if (!loading) {
      guardarPreferenciasUsuario();
    }
  }, [vistaTabla, filtro, loading]); // Fix for the missing dependency warning

  // Función para alternar la visualización de detalles
  // eslint-disable-next-line no-unused-vars
  const toggleDetalles = (id) => {
    if (expandedViajeId === id) {
      setExpandedViajeId(null);
    } else {
      setExpandedViajeId(id);
    }
  };

  // Effect to fetch current user's department and admin status from Firestore
  useEffect(() => {
    const fetchUserDepartment = async () => {
      if (!auth.currentUser) return;

      try {
        const userRef = doc(db, 'levapan_usuarios', auth.currentUser.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();

          // Check if user is admin by role OR by department
          const userIsAdmin = userData.rol === 'admin' ||
            userData.isAdmin === true ||
            userData.admin === true ||
            userData.departamento === 'Admin'; // Incluir departamento "Admin" como indicador de administrador

          setIsAdmin(userIsAdmin);
          console.log("Usuario es admin:", userIsAdmin);

          // Si es admin o del departamento Admin, siempre mostrar todos los viajes
          // sin opción de filtrar por departamento
          if (userIsAdmin || userData.departamento === 'Admin') {
            setFilterByUserDept(false);
          }

          // Guardar el departamento para referencia
          if (userData.departamento) {
            setUserDepartment(userData.departamento);
            console.log("Usuario departamento encontrado:", userData.departamento);
          }

          // Trigger search when user data is loaded
          setTriggerSearch(prev => prev + 1);
        }
      } catch (err) {
        console.error("Error fetching user department:", err);
      }
    };

    fetchUserDepartment();
  }, []);

  // Enhanced matchesFilters function with admin bypass for department "Admin"
  const matchesFilters = (viaje) => {
    // Si el usuario es del departamento Admin o tiene rol admin, mostrar todos los departamentos sin filtro
    if ((isAdmin || userDepartment === 'Admin')) {
      // Skip department filtering completely for users with department "Admin"
      // No necesitamos verificar filterByUserDept porque siempre será false para Admin
    } else {
      // Department filtering logic for regular users
      let matchesDepartamento = true;

      if (departamentoFilter) {
        // If there's a specific department filter, use that first
        const departamentoValue = departamentoFilter.trim().toLowerCase();
        const viajeDepartamento = viaje.departamento ||
          (viaje.solicitante?.departamento || '')
          || '';

        matchesDepartamento = viajeDepartamento.toLowerCase().includes(departamentoValue);
      }
      // If no explicit department filter but user department filtering is enabled
      else if (filterByUserDept && userDepartment) {
        const viajeDepartamento = viaje.departamento ||
          (viaje.solicitante?.departamento || '')
          || '';

        // Only include trips matching user's department (case insensitive)
        if (viajeDepartamento) {
          matchesDepartamento = viajeDepartamento.toLowerCase() === userDepartment.toLowerCase();
        } else {
          // If trip has no department info, exclude it when filtering by department
          return false;
        }
      }

      // If doesn't match department, no need to check other filters
      if (!matchesDepartamento) return false;
    }

    // Continue with date filters
    let matchesDate = true;
    if (fechaInicio || fechaFin) {
      let startDate = null;
      let endDate = null;

      // Helper function to parse date from different formats
      const parseFilterDate = (dateString) => {
        if (!dateString) return null;

        // Try to parse DD/MM/YYYY or DD-MM-YYYY format
        if (dateString.includes('/') || dateString.includes('-')) {
          const separator = dateString.includes('/') ? '/' : '-';
          const parts = dateString.split(separator);

          if (parts.length === 3) {
            // Check if first part is day or year
            if (parts[0].length === 4) {
              // YYYY-MM-DD format
              return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
            } else {
              // DD-MM-YYYY or DD/MM/YYYY format
              return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
            }
          }
        }

        // Fallback to standard parsing
        return new Date(dateString);
      };

      if (fechaInicio) {
        startDate = parseFilterDate(fechaInicio);
        if (startDate) startDate.setHours(0, 0, 0, 0);
      }

      if (fechaFin) {
        endDate = parseFilterDate(fechaFin);
        if (endDate) endDate.setHours(23, 59, 59, 999);
      }

      // Parse the viaje date
      let viajeDate = null;

      if (viaje.fecha) {
        try {
          // Try parsing DD-MM-YYYY format
          if (typeof viaje.fecha === 'string' && viaje.fecha.includes('-')) {
            const parts = viaje.fecha.split('-');
            if (parts.length === 3) {
              viajeDate = new Date(parts[2], parts[1] - 1, parts[0]);
            }
          } else {
            // Try standard date parsing
            viajeDate = new Date(viaje.fecha);
          }
        } catch (e) {
          console.error("Error parsing date", viaje.fecha);
        }
      }

      if (!viajeDate && viaje.viajes) {
        try {
          // Try parsing from viajes field
          const parts = viaje.viajes.split(' ')[0].split('-');
          if (parts.length === 3) {
            viajeDate = new Date(parts[2], parts[1] - 1, parts[0]);
          }
        } catch (e) {
          console.error("Error parsing viajes date", viaje.viajes);
        }
      }

      if (!viajeDate) {
        matchesDate = false;
      } else {
        // Check date range
        const isAfterStart = startDate ? viajeDate >= startDate : true;
        const isBeforeEnd = endDate ? viajeDate <= endDate : true;

        matchesDate = isAfterStart && isBeforeEnd;
      }
    }

    // Check CC Cliente filter
    let matchesCcCliente = true;
    if (ccClienteFilter) {
      const ccClienteValue = ccClienteFilter.trim().toLowerCase();
      const viajeCC = viaje.cc_cliente ||
        (viaje.solicitante?.cc_cliente || '')
        || '';

      matchesCcCliente = viajeCC.toLowerCase().includes(ccClienteValue);
    }

    return matchesDate && matchesCcCliente;
  };

  useEffect(() => {
    const fetchViajes = async () => {
      if (!auth.currentUser) return;

      try {
        setLoading(true);
        setError(null);

        console.log("Fetching viajes with filters - Department:",
          filterByUserDept ? userDepartment : "All departments");

        let todosViajes = [];

        // Helper function to parse date from different formats (reused from matchesFilters)
        const parseFilterDate = (dateString) => {
          if (!dateString) return null;

          // Try to parse DD/MM/YYYY or DD-MM-YYYY format
          if (dateString.includes('/') || dateString.includes('-')) {
            const separator = dateString.includes('/') ? '/' : '-';
            const parts = dateString.split(separator);

            if (parts.length === 3) {
              // Check if first part is day or year
              if (parts[0].length === 4) {
                // YYYY-MM-DD format
                return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
              } else {
                // DD-MM-YYYY or DD/MM/YYYY format
                return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
              }
            }
          }

          // Fallback to standard parsing
          return new Date(dateString);
        };

        // Parse date filters if provided
        let startDate = null;
        let endDate = null;

        if (fechaInicio) {
          startDate = parseFilterDate(fechaInicio);
          if (startDate) startDate.setHours(0, 0, 0, 0);
        }

        if (fechaFin) {
          endDate = parseFilterDate(fechaFin);
          if (endDate) endDate.setHours(23, 59, 59, 999);
        }

        // El historial de este panel es independiente: solo consume los
        // documentos archivados por la aplicación al aprobar un viaje.
        try {
          const viajesRef = collection(db, 'levapan_approval_history');

          // Sin limit para garantizar que se traigan TODOS los documentos.
          // Un limit sin orderBy devuelve docs en orden lexicográfico por ID,
          // lo que excluye aleatoriamente registros válidos al cortar la colección.
          const q = query(viajesRef);

          const snapshot = await getDocs(q);
          let viajesDetallados = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            tipoOrigen: 'detallado'
          }));

          console.log(`📊 Documentos recuperados de levapan_approval_history: ${viajesDetallados.length}`);

          // Protección adicional: aunque la colección sea de historial,
          // nunca mostrar documentos que no tengan el estado aprobado.
          viajesDetallados = viajesDetallados.filter(v => {
            return (v.pedido || '').toString().trim().toLowerCase() === 'aprobado';
          }).map(viaje => {
            // Add visual indicators for group trips
            const isGrupal = viaje.tipo === 'ViejeGrupal' ||
              (viaje.pasajeros && Array.isArray(viaje.pasajeros) && viaje.pasajeros.length > 0) ||
              (viaje.pasajerosInfo && Array.isArray(viaje.pasajerosInfo) && viaje.pasajerosInfo.length > 0);

            // Add a convenience property for UI display
            return {
              ...viaje,
              isGroupTrip: isGrupal,
              pasajerosCount: isGrupal ?
                (viaje.pasajeros?.length || viaje.pasajerosInfo?.length || 0) : 0,
              hasRequester: !!viaje.solicitante,
              requesterInfo: viaje.solicitante ? {
                name: viaje.solicitante.nombre || 'Sin nombre',
                ccCliente: viaje.solicitante.cc_cliente || 'N/A1',
                cedula: viaje.solicitante.cedula || 'N/A2',
                codeDireccion: viaje.solicitante.code_direccion || 'N/A3'
              } : null
            };
          });

          // Apply all filters using the helper function
          viajesDetallados = viajesDetallados.filter(matchesFilters);

          // Apply existing 'taximetro'/'reserva' filter if needed
          console.log(`🔍 Aplicando filtro: "${filtro}"`);
          if (filtro === 'taximetro') {
            viajesDetallados = viajesDetallados.filter(v => {
              // Check if tipoPedido exists and matches
              if (v.tipoPedido === 'Taximetro') return true;
              // Fallback to checking viajes field if tipoPedido is empty
              if (!v.tipoPedido && v.viajes && v.viajes.toUpperCase().includes('TAXIMETRO')) return true;
              return false;
            });
          } else if (filtro === 'reserva') {
            viajesDetallados = viajesDetallados.filter(v => {
              // Check if tipoPedido exists and matches
              if (v.tipoPedido === 'Reserva') {
                console.log(`✓ Incluido por tipoPedido 'Reserva': ${v.id.substring(0, 8)}`);
                return true;
              }
              // Fallback to checking viajes field if tipoPedido is empty
              if (!v.tipoPedido && v.viajes && v.viajes.toUpperCase().includes('RESERVA')) {
                console.log(`✓ Incluido por campo viajes: ${v.id.substring(0, 8)} - tipoPedido vacío, viajes contiene RESERVA`);
                return true;
              }
              console.log(`✗ Excluido: ${v.id.substring(0, 8)} - tipoPedido: "${v.tipoPedido}", viajes: "${v.viajes}"`);
              return false;
            });
          }

          todosViajes = [...viajesDetallados];
        } catch (err) {
          console.warn("Error al obtener 'levapan_approval_history':", err);
        }

        // Ordenar los viajes por fecha (más reciente primero)
        // Optimized sorting with better date extraction
        todosViajes.sort((a, b) => {
          const dateA = obtenerFechaOrdenacion(a);
          const dateB = obtenerFechaOrdenacion(b);

          // Si ambas fechas son válidas, comparar
          if (dateA && dateB) {
            return dateB.getTime() - dateA.getTime(); // Orden descendente (nuevos primero)
          }

          // Si alguna fecha falta, colocar al final
          if (!dateA && dateB) return 1;
          if (dateA && !dateB) return -1;

          // Si ambas fechas faltan, mantener el orden original
          return 0;
        });

        setViajes(todosViajes);
      } catch (err) {
        console.error("Error general al obtener historial de viajes:", err);
        setError("Ocurrió un error al cargar tu historial de viajes.");
      } finally {
        setLoading(false);
      }
    };

    // Función auxiliar para obtener la fecha de diferentes campos en orden de prioridad
    function obtenerFechaOrdenacion(viaje) {
      // Intentar obtener la fecha de diferentes campos en orden de prioridad
      let fechaObj = null;

      try {
        // 1. Primero intentar con el campo fecha (formato DD-MM-YYYY)
        if (viaje.fecha) {
          fechaObj = parseFechaString(viaje.fecha);
        }

        // 2. Luego probar con el campo viajes (formato DD-MM-YYYY HH:MM)
        if (!fechaObj && viaje.viajes) {
          fechaObj = parseViajesDate(viaje.viajes);
        }

        // 3. Probar con campo fechaHora
        if (!fechaObj && viaje.fechaHora) {
          fechaObj = parseFechaString(viaje.fechaHora);
        }

        // 4. Como último recurso, usar createdAt
        if (!fechaObj && viaje.createdAt) {
          if (viaje.createdAt instanceof Date) {
            fechaObj = viaje.createdAt;
          } else if (viaje.createdAt?.toDate) {
            fechaObj = viaje.createdAt.toDate();
          } else if (typeof viaje.createdAt === 'number' || typeof viaje.createdAt === 'string') {
            fechaObj = new Date(viaje.createdAt);
          }
        }
      } catch (err) {
        // Si hay algún error, retornar null y el viaje se ordenará al final
        console.warn("Error al procesar fecha para ordenación:", err);
      }

      return fechaObj;
    }

    fetchViajes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro, triggerSearch, userDepartment, filterByUserDept]); // Add userDepartment and filterByUserDept to dependencies

  // Improved parseViajesDate function for better date parsing
  const parseViajesDate = (dateString) => {
    if (!dateString) return null;

    try {
      // Improved format handling: "DD-MM-YYYY HH:MM"
      if (typeof dateString !== 'string') {
        console.warn("parseViajesDate - Expected string but got:", typeof dateString);
        return null;
      }

      let datePart, timePart;

      if (dateString.includes(' ')) {
        [datePart, timePart] = dateString.split(' ');
      } else {
        datePart = dateString;
      }

      let day, month, year;

      if (datePart.includes('-')) {
        [day, month, year] = datePart.split('-');
      } else if (datePart.includes('/')) {
        [day, month, year] = datePart.split('/');
      } else {
        return null;
      }

      // Ensure values are valid
      day = parseInt(day, 10);
      month = parseInt(month, 10) - 1; // JS months are 0-indexed
      year = parseInt(year, 10);

      if (isNaN(day) || isNaN(month) || isNaN(year)) return null;

      if (timePart) {
        const [hour, minute] = timePart.split(':');
        return new Date(year, month, day, parseInt(hour, 10), parseInt(minute, 10));
      } else {
        return new Date(year, month, day);
      }
    } catch (e) {
      console.error("Error parsing viajes date:", e);
      return null;
    }
  };

  // Enhanced parseFechaString function to better handle DD-MM-YYYY format
  const parseFechaString = (dateString) => {
    if (!dateString) return null;

    try {
      // Log for debugging
      console.log("Parsing date:", dateString);

      if (typeof dateString !== 'string') {
        // Handle non-string types
        if (dateString instanceof Date) return dateString;
        if (dateString?.toDate) return dateString.toDate();
        return null;
      }

      // For DD-MM-YYYY format (prioritize this format)
      if (dateString.includes('-')) {
        const parts = dateString.split('-');
        if (parts.length === 3) {
          // Explicitly handle DD-MM-YYYY format first
          if (parts[0].length <= 2 && parseInt(parts[0]) <= 31) {
            // DD-MM-YYYY format (common in Spanish)
            const year = parseInt(parts[2], 10);
            const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed
            const day = parseInt(parts[0], 10);

            // Validate date components
            if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
              return new Date(year, month, day);
            }
          }
          // Then try YYYY-MM-DD format
          else if (parts[0].length === 4) {
            // YYYY-MM-DD format
            return new Date(parts[0], parts[1] - 1, parts[2]);
          }
        }
      } else if (dateString.includes('/')) {
        const parts = dateString.split('/');
        if (parts.length === 3) {
          // Assume DD/MM/YYYY format
          return new Date(parts[2], parts[1] - 1, parts[0]);
        }
      }

      // Fallback to standard parsing
      const parsedDate = new Date(dateString);
      return isNaN(parsedDate.getTime()) ? null : parsedDate;
    } catch (e) {
      console.error("Error parsing fecha string:", e, dateString);
      return null;
    }
  };

  // Formatear fecha para mostrar
  const formatDate = (viaje) => {
    if (!viaje) return 'Fecha no disponible';

    try {
      if (viaje.viajes) {
        return viaje.viajes; // "16-04-2025 11:38" formato original
      } else if (viaje.fecha) {
        return viaje.fecha; // Usar fecha en formato original
      } else {
        return 'Fecha no disponible';
      }
    } catch (e) {
      return 'Fecha no disponible';
    }
  };

  // Función para formatear valores monetarios
  const formatarValor = (valor) => {
    if (!valor) return 'N/A';
    return `$${parseFloat(valor).toFixed(2)}`;
  };

  // Función para mostrar iconos especiales en la tabla
  // eslint-disable-next-line no-unused-vars
  const mostrarIconoRandon = (valor) => {
    if (!valor) return null;

    if (valor.startsWith(' #️⃣')) {
      return valor.replace(' #️⃣', '');
    } else if (valor.startsWith('B') || valor.startsWith('A') || valor.startsWith('C')) {
      return (
        <span className="icon-randon">
          <i className="fas fa-hashtag"></i> {valor}
        </span>
      );
    } else if (valor.includes('OJO RESERVA')) {
      return (
        <span className="icon-ojo">
          <i className="fas fa-eye"></i> {valor.replace('¡OJO RESERVA!', '')}
        </span>
      );
    }
    return valor;
  };

  // Formato para fecha en la tabla
  // eslint-disable-next-line no-unused-vars
  const formatFechaTabla = (viaje) => {
    try {
      if (viaje.viajes) {
        return viaje.viajes;
      } else if (viaje.fecha) {
        return viaje.fecha;
      }
      return '-';
    } catch (e) {
      return '-';
    }
  };

  // Función para exportar a Excel
  const exportarExcel = () => {
    const rows = viajes.map(getConsolidatedRow);
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 28 }, { wch: 20 },
      { wch: 18 }, { wch: 14 }, { wch: 32 }, { wch: 32 }, { wch: 12 },
      { wch: 28 }, { wch: 12 }, { wch: 34 }, { wch: 36 }, { wch: 36 }
    ];
    if (ws['!ref']) ws['!autofilter'] = { ref: ws['!ref'] };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Consolidado');
    XLSX.writeFile(wb, `Reporte_Consolidado_Viajes_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const getConsolidatedRow = (viaje) => {
    const firstPassenger = getPassengers(viaje)[0] || {};
    const firstNonEmpty = (...values) => values.find(value =>
      value !== undefined && value !== null && String(value).trim() !== ''
    );
    const rawValue = firstNonEmpty(viaje.valor, viaje.montoTotalCalculado, viaje.valor2);
    const numericValue = rawValue == null
      ? ''
      : Number(String(rawValue).replace(/[^\d.-]/g, ''));

    return {
      'Fecha': formatDateForTable(viaje),
      'Hora inicio': formatTimeForTable(viaje),
      'Cédula': firstNonEmpty(viaje.cedula, firstPassenger.cedula, firstPassenger.area, firstPassenger.aera, viaje.area) || 'N/A',
      'Nombre usuario': firstNonEmpty(viaje.nombreCliente, viaje.nombre, firstPassenger.nombre, firstPassenger.nombre_cliente) || 'N/A',
      'Localidad': firstNonEmpty(viaje.localidad, firstPassenger.localidad, firstPassenger.direccion, viaje.solicitante?.localidad, viaje.departamento, firstPassenger.departamento, viaje.solicitante?.departamento) || 'N/A',
      'División': firstNonEmpty(viaje.division, viaje.Division, firstPassenger.division, firstPassenger.Division, viaje.solicitante?.division, viaje.solicitante?.Division) || 'N/A',
      'CECOS': firstNonEmpty(viaje.cecos, viaje.cc_depart, viaje.cc_cliente, viaje.cc, viaje.CC, firstPassenger.cecos, firstPassenger.cc_depart, firstPassenger.cc_cliente, viaje.solicitante?.cecos, viaje.solicitante?.cc_depart, viaje.solicitante?.cc_cliente) || 'N/A',
      'Origen': firstNonEmpty(viaje.direccion, viaje.origen) || 'N/A',
      'Destino': firstNonEmpty(viaje.destino, viaje.destinofinal) || 'N/A',
      'Unidad': firstNonEmpty(viaje.unidad, viaje.conductorUnidad) || 'N/A',
      'Solicitante': firstNonEmpty(viaje.solicitante?.nombre, viaje.nombreSolicitante, viaje.userEmail, viaje.approvedByEmail) || 'Automático',
      'Valor': Number.isFinite(numericValue) ? numericValue : String(rawValue || ''),
      'Ocupante': firstNonEmpty(viaje.ocupantes, getFormattedPassengers(viaje)) || 'N/A',
      'Comentario': firstNonEmpty(viaje.comentarioConductor, viaje.observacion) || 'N/A',
      'Nota': firstNonEmpty(viaje.nota, viaje.departamentoComentario, viaje.comentarioDepartamento, viaje.comentario, viaje.motivo) || 'N/A'
    };
  };

  // Define the missing cambiarVista function
  const cambiarVista = (esTabla) => {
    setVistaTabla(esTabla);
  };

  // Helper to determine if a trip is a group trip
  const isGroupTrip = (viaje) => {
    return viaje.tipo === 'ViejeGrupal' ||
      (viaje.pasajeros && Array.isArray(viaje.pasajeros) && viaje.pasajeros.length > 0) ||
      (viaje.pasajerosInfo && Array.isArray(viaje.pasajerosInfo) && viaje.pasajerosInfo.length > 0);
  };

  // Format date for table display (DD/MM/YYYY)
  const formatDateForTable = (viaje) => {
    if (!viaje) return 'N/A';

    // Try to extract date from different possible fields
    if (viaje.fecha) {
      return viaje.fecha; // Already in DD-MM-YYYY format
    } else if (viaje.viajes && typeof viaje.viajes === 'string') {
      // Extract date part from "DD-MM-YYYY HH:MM" format
      const datePart = viaje.viajes.split(' ')[0];
      if (datePart) return datePart;
    } else if (viaje.createdAt) {
      // Handle Firebase Timestamp or Date object
      try {
        const date = viaje.createdAt instanceof Date
          ? viaje.createdAt
          : viaje.createdAt.toDate ? viaje.createdAt.toDate() : new Date(viaje.createdAt);
        return date.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      } catch (e) {
        console.error('Error formatting date:', e);
      }
    }
    return 'N/A';
  };

  // Format time for table display (HH:MM)
  const formatTimeForTable = (viaje) => {
    if (!viaje) return 'N/A';

    // Try to extract time from different possible fields
    if (viaje.tiempoInicioTaximetro) {
      return viaje.tiempoInicioTaximetro;
    } else if (viaje.hora_reserva) {
      return viaje.hora_reserva;
    } else if (viaje.viajes && typeof viaje.viajes === 'string') {
      // Extract time part from "DD-MM-YYYY HH:MM" format
      const parts = viaje.viajes.split(' ');
      if (parts.length > 1) return parts[1];
    } else if (viaje.createdAt) {
      // Handle Firebase Timestamp or Date object
      try {
        const date = viaje.createdAt instanceof Date
          ? viaje.createdAt
          : viaje.createdAt.toDate ? viaje.createdAt.toDate() : new Date(viaje.createdAt);
        return date.toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        console.error('Error formatting time:', e);
      }
    }
    return 'N/A';
  };

  // Helper to get passengers array from either format
  const getPassengers = (viaje) => {
    if (viaje.pasajeros && Array.isArray(viaje.pasajeros)) {
      return viaje.pasajeros;
    } else if (viaje.pasajerosInfo && Array.isArray(viaje.pasajerosInfo)) {
      return viaje.pasajerosInfo;
    }
    return [];
  };

  // Helper to get passenger count
  const getPassengerCount = (viaje) => {
    const passengers = getPassengers(viaje);
    return passengers.length;
  };

  // Helper to get trip type display text
  const getTripTypeDisplay = (viaje) => {
    if (isGroupTrip(viaje)) {
      return "Viaje Grupal";
    } else if (viaje.tipoPedido === 'Reserva') {
      return "Reserva";
    } else if (viaje.tipoPedido === 'Taximetro') {
      return "Taxímetro";
    }
    return viaje.tipoPedido || viaje.tipo || "Viaje";
  };

  // Add function to close the passenger modal
  const closePassengersModal = () => {
    setPassengerModalOpen(false);
    setSelectedPassengers(null);
    setSelectedTripData(null);
  };

  // OPTION 1: Remove the unused function
  // Delete the entire getSolicitante function that's not being used

  // OPTION 2: Keep the function with ESLint disable comment
  // eslint-disable-next-line no-unused-vars
  const getSolicitante = (viaje) => {
    return viaje.solicitante || null;
  };

  // Helper to check if there's requester info
  const hasSolicitanteInfo = (viaje) => {
    return viaje.solicitante && (
      viaje.solicitante.nombre ||
      viaje.solicitante.cc_cliente ||
      viaje.solicitante.cedula ||
      viaje.solicitante.code_direccion ||
      viaje.solicitante.telefono ||
      viaje.solicitante.email
    );
  };

  // Add state to track which trips have their passenger details expanded with ESLint disable
  // eslint-disable-next-line no-unused-vars
  const [expandedPassengersList, setExpandedPassengersList] = useState([]);

  // Function to toggle passenger details for a specific trip with ESLint disable
  // eslint-disable-next-line no-unused-vars
  const togglePassengerDetails = (viajeId) => {
    setExpandedPassengersList(prev =>
      prev.includes(viajeId)
        ? prev.filter(id => id !== viajeId)
        : [...prev, viajeId]
    );
  };

  // Helper function to render group trip details in the modal with ESLint disable
  // eslint-disable-next-line no-unused-vars
  const renderGroupTripDetails = (viaje) => {
    if (!viaje) return null;

    const pasajeros = getPassengers(viaje);

    return (
      <>
        <div className="grupo-trip-info">
          <div className="grupo-trip-row">
            <div className="grupo-trip-label">Origen:</div>
            <div className="grupo-trip-value">{viaje.direccion || viaje.origen || 'No especificado'}</div>
          </div>
          <div className="grupo-trip-row">
            <div className="grupo-trip-label">Destino:</div>
            <div className="grupo-trip-value">{viaje.destino || viaje.destinofinal || 'No especificado'}</div>
          </div>
          <div className="grupo-trip-row">
            <div className="grupo-trip-label">Fecha y Hora:</div>
            <div className="grupo-trip-value">{formatDate(viaje)}</div>
          </div>
        </div>

        <div className="grupo-pasajeros-header">
          <i className="fas fa-users"></i>
          <h4>Pasajeros ({pasajeros.length})</h4>
        </div>

        <div className="grupo-pasajeros-list">
          {pasajeros.map((pasajero, idx) => (
            <div key={pasajero.id || idx} className="grupo-pasajero-item">
              <div className="grupo-pasajero-index">{idx + 1}</div>
              <div className="grupo-pasajero-data">
                <div className="grupo-pasajero-name">{pasajero.nombre || 'Sin nombre'}</div>
                <div className="grupo-pasajero-details">
                  {pasajero.cc_cliente && (
                    <div className="grupo-pasajero-detail">
                      <span className="detail-label">CC Cliente:</span>
                      <span className="detail-value">{pasajero.cc_cliente}</span>
                    </div>
                  )}
                  {pasajero.aera && (
                    <div className="grupo-pasajero-detail">
                      <span className="detail-label">C.I:</span>
                      <span className="detail-value">{pasajero.cedula}</span>
                    </div>
                  )}
                  {pasajero.code_direccion && (
                    <div className="grupo-pasajero-detail">
                      <span className="detail-label">Cód. Dirección:</span>
                      <span className="detail-value">{pasajero.code_direccion}</span>
                    </div>
                  )}
                  {pasajero.telefono && (
                    <div className="grupo-pasajero-detail">
                      <span className="detail-label">Teléfono:</span>
                      <span className="detail-value">{pasajero.telefono}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {hasSolicitanteInfo(viaje) && (
          <div className="grupo-solicitante-section">
            <div className="grupo-solicitante-header">
              <i className="fas fa-user-tag"></i>
              <h4>Solicitado por</h4>
            </div>
            <div className="grupo-solicitante-data">
              <div className="grupo-solicitante-name">{viaje.solicitante.nombre || "Usuario"}</div>
              <div className="grupo-solicitante-details">
                {viaje.solicitante.cc_cliente && (
                  <div className="grupo-solicitante-detail">
                    <span className="detail-label">CC Cliente:</span>
                    <span className="detail-value">{viaje.solicitante.cc_cliente}</span>
                  </div>
                )}
                {viaje.solicitante.cedula && (
                  <div className="grupo-solicitante-detail">
                    <span className="detail-label">Cédula:</span>
                    <span className="detail-value">{viaje.solicitante.cedula}</span>
                  </div>
                )}
                {viaje.solicitante.code_direccion && (
                  <div className="grupo-solicitante-detail">
                    <span className="detail-label">Cód. Dirección:</span>
                    <span className="detail-value">{viaje.solicitante.code_direccion}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // Helper to format passenger names and codes for display
  const getFormattedPassengers = (viaje) => {
    if (!isGroupTrip(viaje)) {
      return viaje.numeroOcupantes || viaje.ocupantes || '1';
    }

    const passengers = getPassengers(viaje);
    if (!passengers.length) return 'Sin pasajeros';

    return passengers.map(p => {
      const name = p.nombre || 'Sin nombre';
      const code = p.cc_cliente || p.aera || p.code_direccion;
      return code ? `${name} (${code})` : name;
    }).join(', ');
  };

  // Fix 2: Define the missing getTripTypeClass function
  const getTripTypeClass = (viaje) => {
    if (isGroupTrip(viaje)) {
      return "viajegrupal";
    } else if (viaje.tipoPedido?.toLowerCase() === 'reserva') {
      return "reserva";
    } else if (viaje.tipoPedido?.toLowerCase() === 'taximetro') {
      return "taximetro";
    }
    return "default";
  };

  // Add the missing pagination functions
  const goToPage = (pageNumber) => {
    // Ensure page number is within valid range
    if (pageNumber < 1) pageNumber = 1;
    if (pageNumber > totalPages) pageNumber = totalPages;
    setCurrentPage(pageNumber);
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Add a function to calculate the total value of trips
  const calculateTotalValue = (trips) => {
    return trips.reduce((total, viaje) => {
      // Parse the value - handle different formats and types
      const valorNumerico = viaje.valor ?
        // Remove currency symbol if present and convert to float
        parseFloat(String(viaje.valor).replace(/[^\d.-]/g, '')) : 0;

      return isNaN(valorNumerico) ? total : total + valorNumerico;
    }, 0);
  };

  // Add function to open passenger details modal
  // Conservada para reactivar el detalle interactivo si vuelve la vista de acciones.
  // eslint-disable-next-line no-unused-vars
  const openPassengersModal = (viaje) => {
    const passengers = getPassengers(viaje);
    if (passengers && passengers.length > 0) {
      setSelectedPassengers(passengers);
      setSelectedTripData({
        origen: viaje.direccion || viaje.origen || 'No especificado',
        destino: viaje.destino || viaje.destinofinal || 'No especificado',
        fecha: formatDate(viaje),
        solicitante: viaje.solicitante
      });
      setPassengerModalOpen(true);
    }
  };

  // Add functions for editing trips
  const handleEditClick = (viaje) => {
    setEditingViaje(viaje);
    setEditFormData({
      hora: viaje.hora || formatTimeForTable(viaje) || '',
      nombreCliente: getFirstPassengerData(viaje, 'nombre') || '',
      cc_cliente: getFirstPassengerData(viaje, 'cc_cliente') || '',
      cedula: getFirstPassengerData(viaje, 'area') || '',
      departamento: getFirstPassengerData(viaje, 'departamento') || '',
      direccion: viaje.direccion || '',
      destino: viaje.destino || viaje.destinofinal || '',
      tiempoFinalTaximetro: viaje.tiempoFinalTaximetro || '',
      valor: viaje.valor || '',
      unidad: viaje.unidad || ''
    });
    setEditModalOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingViaje(null);
    setEditFormData({});
  };

  const handleSaveEdit = async () => {
    if (!editingViaje || !editingViaje.id) return;

    try {
      setIsSaving(true);
      const tripRef = doc(db, 'levapan_approval_history', editingViaje.id);

      // Prepare update object
      const updates = {
        hora: editFormData.hora,
        nombreCliente: editFormData.nombreCliente,
        cc_cliente: editFormData.cc_cliente,
        aera: editFormData.cedula, // Map cedula back to aera field
        departamento: editFormData.departamento,
        direccion: editFormData.direccion,
        destino: editFormData.destino,
        tiempoFinalTaximetro: editFormData.tiempoFinalTaximetro,
        valor: editFormData.valor,
        unidad: editFormData.unidad
      };

      // Also update first passenger if exists for consistency
      if (editingViaje.pasajeros && Array.isArray(editingViaje.pasajeros) && editingViaje.pasajeros.length > 0) {
        const updatedPasajeros = [...editingViaje.pasajeros];
        updatedPasajeros[0] = {
          ...updatedPasajeros[0],
          nombre: editFormData.nombreCliente,
          cc_cliente: editFormData.cc_cliente,
          cedula: editFormData.cedula,
          departamento: editFormData.departamento
        };
        updates.pasajeros = updatedPasajeros;
      }

      await updateDoc(tripRef, updates);

      // Update local state
      setViajes(prevViajes => prevViajes.map(v =>
        v.id === editingViaje.id ? { ...v, ...updates } : v
      ));

      closeEditModal();
      alert('Viaje actualizado correctamente');
    } catch (err) {
      console.error("Error al actualizar viaje:", err);
      alert('Error al actualizar el viaje: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicateClick = async (viaje) => {
    if (!window.confirm('¿Estás seguro de que deseas duplicar este registro?')) return;

    try {
      setIsSaving(true);

      // Helper function to sanitize object (Firestore doesn't allow undefined)
      const sanitizeData = (obj) => {
        const sanitized = {};
        Object.keys(obj).forEach(key => {
          if (obj[key] !== undefined) {
            if (obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
              sanitized[key] = sanitizeData(obj[key]);
            } else if (Array.isArray(obj[key])) {
              sanitized[key] = obj[key].map(item =>
                (item && typeof item === 'object') ? sanitizeData(item) : item
              ).filter(item => item !== undefined);
            } else {
              sanitized[key] = obj[key];
            }
          }
        });
        return sanitized;
      };

      // Create a copy of the trip without the original ID
      // eslint-disable-next-line no-unused-vars
      const { id, ...originalData } = viaje;

      // Sanitize data before sending to Firestore
      const viajeData = sanitizeData(originalData);

      // Add fresh metadata
      const dataToSave = {
        ...viajeData,
        empresa: appConfig.companyCode,
        fechaCreacion: new Date().toISOString(),
        duplicadoDe: id || 'unknown'
      };

      const docRef = await addDoc(collection(db, 'levapan_approval_history'), dataToSave);

      // Add to local list at the beginning
      const nuevoViaje = { id: docRef.id, ...dataToSave };
      setViajes(prevViajes => [nuevoViaje, ...prevViajes]);

      alert('Registro duplicado correctamente');
    } catch (err) {
      console.error("Error al duplicar viaje:", err);
      alert('Error al duplicar el viaje: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };




  // Helper to get the first passenger's data or fallback to other sources
  const getFirstPassengerData = (viaje, field) => {
    // For nombre field, check specific name fields first
    if (field === 'nombre') {
      // Try to get nombre from first passenger
      if (viaje.pasajeros && Array.isArray(viaje.pasajeros) && viaje.pasajeros.length > 0) {
        return viaje.pasajeros[0].nombre || viaje.pasajeros[0].nombre_cliente || '';
      }

      // Fallback to pasajerosInfo
      if (viaje.pasajerosInfo && Array.isArray(viaje.pasajerosInfo) && viaje.pasajerosInfo.length > 0) {
        return viaje.pasajerosInfo[0].nombre || viaje.pasajerosInfo[0].nombre_cliente || '';
      }

      // Fallback to viaje direct fields
      return viaje.nombreCliente || viaje.nombre || '';
    }

    // Handle area field specifically
    if (field === 'area') {
      // Try to get area from first passenger
      if (viaje.pasajeros && Array.isArray(viaje.pasajeros) && viaje.pasajeros.length > 0) {
        return viaje.pasajeros[0].cedula || viaje.pasajeros[0].cedula || '';
      }

      // Fallback to pasajerosInfo
      if (viaje.pasajerosInfo && Array.isArray(viaje.pasajerosInfo) && viaje.pasajerosInfo.length > 0) {
        return viaje.pasajerosInfo[0].cedula || viaje.pasajerosInfo[0].cedula || '';
      }

      // Fallback to viaje direct field
      return viaje.area || viaje.cedula || '';
    }

    // Try to get data from first passenger
    if (viaje.pasajeros && Array.isArray(viaje.pasajeros) && viaje.pasajeros.length > 0) {
      return viaje.pasajeros[0][field] || '';
    }

    // Fallback to pasajerosInfo if available
    if (viaje.pasajerosInfo && Array.isArray(viaje.pasajerosInfo) && viaje.pasajerosInfo.length > 0) {
      return viaje.pasajerosInfo[0][field] || '';
    }

    // Fallback to viaje direct field
    if (viaje[field]) {
      return viaje[field];
    }

    return 'N/A';
  };

  // Enhanced pagination utilities 
  const applyViewLimit = (items) => {
    // Apply the view limit to avoid loading too many items
    return items.slice(0, maxViewItems);
  };

  // Get current page items with view limit applied
  const getCurrentPageItems = () => {
    const limitedViajes = applyViewLimit(viajes);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return limitedViajes.slice(startIndex, endIndex);
  };

  // Total number of pages based on limited view
  const totalItems = Math.min(viajes.length, maxViewItems);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <div className="historial-container">
      <NavBar />
      <div className="historial-content">
        {/* Nuevo selector de vista con persistencia */}
        <div className="vista-controles">
          <button
            className={`vista-btn ${!vistaTabla ? 'active' : ''}`}
            onClick={() => {
              cambiarVista(false);
              localStorage.setItem('userSetViewPreference', 'true');
            }}
          >
            <i className="fas fa-th-large"></i> Vista Tarjetas
          </button>
          <button
            className={`vista-btn ${vistaTabla ? 'active' : ''}`}
            onClick={() => {
              cambiarVista(true);
              localStorage.setItem('userSetViewPreference', 'true');
            }}
          >
            <i className="fas fa-table"></i> Vista Tabla
          </button>
          {/* Added use of guardandoPreferencias state here */}
          {guardandoPreferencias && (
            <div className="guardando-prefs">
              <i className="fas fa-sync fa-spin"></i> Guardando...
            </div>
          )}
        </div>

        {/* Contador de viajes y botón de exportar */}
        {!loading && !error && viajes.length > 0 && (
          <div className="viajes-acciones">
            <div className="viajes-contador">
              <i className="fas fa-list-ul"></i>
              <span>
                Mostrando {viajes.length} viaje(s)
                <span className="viajes-total-value">
                  <i className="fas fa-dollar-sign"></i> Valor Total: {formatarValor(calculateTotalValue(viajes))}
                </span>
                <span className="viajes-sort-indicator">

                  {userDepartment && (


                    <div className="department-filter-section">
                      {(isAdmin || userDepartment === 'Admin') && (
                        <div className="admin-badge"
                          style={{
                            backgroundColor: '#ffd700', color: '#000', padding: '5px 10px',
                            borderRadius: '4px', margin: '5px 0 10px', fontWeight: 'bold',
                            display: 'inline-flex', alignItems: 'center'
                          }}>
                          <i className="fas fa-crown" style={{ marginRight: '8px' }}></i>
                          {userDepartment === 'Admin' ? 'Departamento Administrativo' : 'Acceso de Administrador'} (Acceso Total)
                        </div>
                      )}


                      {/* Mostrar el checkbox de filtro por departamento SOLO si NO es Admin */}
                      {userDepartment !== 'Admin' && !isAdmin && (
                        <div className="filtro-item checkbox-container"
                          style={{
                            marginBottom: '10px', padding: '5px',
                            backgroundColor: filterByUserDept ? '#e3f2fd' : 'transparent',
                            borderRadius: '4px', display: 'flex', alignItems: 'center'
                          }}>
                          <input
                            type="checkbox"
                            id="filterByUserDept"
                            checked={filterByUserDept}
                            onChange={(e) => {
                              setFilterByUserDept(e.target.checked);
                              // Trigger search immediately when toggling department filter
                              setTimeout(() => setTriggerSearch(prev => prev + 1), 0);
                            }}
                            style={{ marginRight: '8px' }}
                          />
                          <label htmlFor="filterByUserDept" style={{ fontWeight: 'bold' }}>
                            Ver <u>SOLO</u> viajes de mi departamento ({userDepartment})
                          </label>
                        </div>
                      )}
                    </div>
                  )}


                </span>
              </span>
            </div>
            {vistaTabla && (
              <button className="exportar-btn" onClick={exportarExcel}>
                <i className="fas fa-file-excel"></i> Exportar a Excel
              </button>


            )}
          </div>
        )}

        {/* Enhanced Filter UI to include Department and CC Cliente */}
        <div className="filtros-fecha-container">


          {/* Department filtering section with admin indication */}


          <div className="filtro-fecha-controls">
            <div className="filtro-item">
              <label htmlFor="fechaInicio">Desde:</label>
              <input
                type="date"
                id="fechaInicio"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
              />
            </div>
            <div className="filtro-item">
              <label htmlFor="fechaFin">Hasta:</label>
              <input
                type="date"
                id="fechaFin"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
              />
            </div>
            <div className="filtro-item">
              <label htmlFor="departamentoFilter">Departamento:</label>
              <input
                type="text"
                id="departamentoFilter"
                value={departamentoFilter}
                onChange={(e) => setDepartamentoFilter(e.target.value)}
                placeholder={userDepartment && filterByUserDept ? `Sobreescribe filtro de ${userDepartment}` : "Filtrar por departamento"}
                disabled={filterByUserDept && userDepartment}
              />
              {filterByUserDept && userDepartment && departamentoFilter &&
                <div className="filter-warning">Desmarca "Solo mi departamento" para usar este filtro</div>
              }
            </div>
            <div className="filtro-item">
              <label htmlFor="ccClienteFilter">CC Cliente:</label>
              <input
                type="text"
                id="ccClienteFilter"
                value={ccClienteFilter}
                onChange={(e) => setCcClienteFilter(e.target.value)}
                placeholder="Filtrar por CC Cliente"
              />
            </div>
            <button className="filtro-btn-buscar" onClick={handleSearchClick}>
              <i className="fas fa-search"></i> Buscar
            </button>
            {(fechaInicio || fechaFin || departamentoFilter || ccClienteFilter) && (
              <button
                className="filtro-btn-reset"
                onClick={() => {
                  setFechaInicio('');
                  setFechaFin('');
                  setDepartamentoFilter('');
                  setCcClienteFilter('');
                  setTriggerSearch(prev => prev + 1);
                }}
              >
                <i className="fas fa-times"></i> Limpiar
              </button>
            )}
          </div>

          {/* Add checkbox to toggle department filtering */}
          {userDepartment && (
            <div className="filtro-item checkbox-container">
              <input
                type="checkbox"
                id="filterByUserDept"
                checked={filterByUserDept}
                onChange={(e) => setFilterByUserDept(e.target.checked)}
              />
              <label htmlFor="filterByUserDept">

              </label>
            </div>
          )}

          {/* User department badge with admin context - Removed for Admin */}
          {userDepartment && filterByUserDept && userDepartment !== 'Admin' && !isAdmin && (
            <div className="user-department-badge"
              style={{
                backgroundColor: '#e3f2fd', color: '#0d47a1', padding: '8px 12px',
                borderRadius: '4px', margin: '10px 0', fontWeight: 'bold', display: 'flex',
                alignItems: 'center', justifyContent: 'center'
              }}>
              <i className="fas fa-filter" style={{ marginRight: '8px' }}></i>
              Mostrando SOLO viajes del departamento: {userDepartment}
            </div>
          )}

          {/* Always show this badge for Admin, since they can't filter by department */}
          {(userDepartment === 'Admin' || isAdmin) && (
            <div className="admin-view-badge"
              style={{
                backgroundColor: '#fff3cd', color: '#856404', padding: '8px 12px',
                borderRadius: '4px', margin: '10px 0', fontWeight: 'bold', display: 'flex',
                alignItems: 'center', justifyContent: 'center'
              }}>
              <i className="fas fa-eye" style={{ marginRight: '8px' }}></i>
              Mostrando viajes de TODOS los departamentos
              {userDepartment === 'Admin' ? ' (Acceso Administrativo Total)' : ' (Acceso Administrativo)'}
            </div>
          )}

          {/* {(fechaInicio || fechaFin || departamentoFilter || ccClienteFilter) && (
            <div className="filtros-activos">
              <span className="filtros-activos-label">Filtros activos:</span>
              {fechaInicio && (
                <span className="filtro-tag fecha">Desde: {fechaInicio}</span>
              )}
              {fechaFin && (
                <span className="filtro-tag fecha">Hasta: {fechaFin}</span>
              )}
              {departamentoFilter && (
                <span className="filtro-tag departamento">Dep: {departamentoFilter}</span>
              )}
              {ccClienteFilter && (
                <span className="filtro-tag cc-cliente">CC: {ccClienteFilter}</span>
              )}
            </div>
          )} */}
        </div>

        {/* Loading state */}
        {loading ? (
          <div className="loading-container">
            <div className="spinner">
              <div className="double-bounce1"></div>
              <div className="double-bounce2"></div>
            </div>
            <p>Cargando historial de viajes...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
          </div>
        ) : viajes.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-route"></i>
            <h2>No hay viajes para mostrar</h2>
            <p>{filterByUserDept && userDepartment
              ? `No se encontraron viajes para el departamento ${userDepartment}`
              : "No se encontraron viajes con los filtros seleccionados"}</p>
          </div>
        ) : vistaTabla ? (
          // Vista Tabla
          <div className="tabla-container">
            <table className="viajes-tabla">
              <thead>
                <tr>
                  <th>Fecha</th><th>Hora inicio</th><th>Cédula</th>
                  <th>Nombre usuario</th><th>Localidad</th><th>División</th>
                  <th>CECOS</th><th>Origen</th><th>Destino</th><th>Unidad</th>
                  <th>Solicitante</th><th>Valor</th><th>Ocupante</th>
                  <th>Comentario</th><th>Nota</th>
                  {HISTORY_ACTIONS_ENABLED && <th>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {getCurrentPageItems().map((viaje) => {
                  const row = getConsolidatedRow(viaje);
                  return (
                    <tr key={viaje.id || viaje.comentarioConductor || Math.random()} className="viaje-fila">
                      {Object.values(row).map((value, index) => (
                        <td key={index} title={String(value)}>{value}</td>
                      ))}
                      {HISTORY_ACTIONS_ENABLED && (
                        <td className="acciones-td">
                          <button className="btn-editar-mini" onClick={() => handleEditClick(viaje)} title="Editar Viaje">
                            <i className="fas fa-edit"></i>
                          </button>
                          <button className="btn-duplicar-mini" onClick={() => handleDuplicateClick(viaje)} title="Duplicar Viaje">
                            <i className="fas fa-copy"></i>
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Enhanced Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination-controls">
                <div className="pagination-info">
                  Mostrando {Math.min(viajes.length, maxViewItems) === viajes.length
                    ? `${(currentPage - 1) * itemsPerPage + 1} - ${Math.min(currentPage * itemsPerPage, viajes.length)} de ${viajes.length}`
                    : `${(currentPage - 1) * itemsPerPage + 1} - ${Math.min(currentPage * itemsPerPage, maxViewItems)} de ${maxViewItems} (limitado de ${viajes.length})`} viajes
                </div>

                <div className="pagination-buttons">
                  <button
                    className="pagination-btn"
                    onClick={prevPage}
                    disabled={currentPage === 1}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>

                  {/* Page number buttons */}
                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    let pageNum;

                    // Calculate which page numbers to show
                    if (totalPages <= 5) {
                      // Show all pages if 5 or fewer
                      pageNum = index + 1;
                    } else if (currentPage <= 3) {
                      // Near the start
                      pageNum = index + 1;
                    } else if (currentPage >= totalPages - 2) {
                      // Near the end
                      pageNum = totalPages - 4 + index;
                    } else {
                      // In the middle
                      pageNum = currentPage - 2 + index;
                    }

                    return (
                      <button
                        key={pageNum}
                        className={`pagination-page ${pageNum === currentPage ? 'active' : ''}`}
                        onClick={() => goToPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  {/* Show ellipsis and last page if many pages */}
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <>
                      <span className="pagination-dots">...</span>
                      <button
                        className="pagination-page"
                        onClick={() => goToPage(totalPages)}
                      >
                        {totalPages}
                      </button>
                    </>
                  )}

                  <button
                    className="pagination-btn"
                    onClick={nextPage}
                    disabled={currentPage === totalPages}
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>

                <div className="items-per-page">
                  <label htmlFor="itemsPerPage">Filas por página:</label>
                  <select
                    id="itemsPerPage"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1); // Reset to first page when changing items per page
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Vista Tarjetas
          <div className="viajes-list">
            {viajes.map((viaje) => (
              <div key={viaje.comentarioConductor} className={`viaje-card ${expandedViajeId === viaje.com ? 'expanded' : ''}`}>
                <div className="viaje-header">
                  <div className="viaje-fecha">
                    <i className="fas fa-calendar-alt"></i>
                    <span>{formatDate(viaje)}</span>
                  </div>
                  <div className="viaje-tipo">
                    <span className={`tipo-badge ${getTripTypeClass(viaje)}`}>
                      {getTripTypeDisplay(viaje)}
                    </span>
                  </div>
                  {viaje.copa ? (
                    <div className="viaje-copa">
                      <i className="fas fa-trophy"></i>
                      <span>{viaje.copa}</span>
                    </div>
                  ) : (
                    <div className="viaje-copa default-copa">
                      <i className="fas fa-map-marker-alt"></i>
                      <span>San gabriel, san juan...</span>
                    </div>
                  )}
                </div>
                <div className="viaje-body">
                  {/* Información compacta siempre visible */}
                  <div className="viaje-resumen">
                    {/* Cliente/pasajero info */}
                    <div className="viaje-cliente">
                      <i className={isGroupTrip(viaje) ? "fas fa-users" : "fas fa-user"}></i>
                      <div>
                        <span className="cliente-label">
                          {isGroupTrip(viaje) ? "Grupo" : "Cliente"}:
                        </span>
                        <span className="cliente-nombre">
                          {isGroupTrip(viaje) ? (
                            <>
                              {getPassengers(viaje)[0]?.nombre || "Sin nombre"} +{getPassengerCount(viaje) - 1}
                            </>
                          ) : (
                            viaje.nombreCliente || viaje.nombre || "Cliente"
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="viaje-puntos">
                      <div className="viaje-punto">
                        <div className="punto-icon origen">
                          <i className="fas fa-map-marker-alt"></i>
                        </div>
                        <div className="punto-info">
                          <div className="punto-label">Origen</div>
                          <div className="punto-valor">{viaje.direccion || 'No especificado'}</div>
                        </div>
                      </div>
                      <div className="viaje-separador">
                        <div className="separador-linea"></div>
                      </div>
                      <div className="viaje-punto">
                        <div className="punto-icon destino">
                          <i className="fas fa-flag-checkered"></i>
                        </div>
                        <div className="punto-info">
                          <div className="punto-label">Destino</div>
                          <div className="punto-valor">{viaje.destino || viaje.destinofinal || 'No especificado'}</div>
                        </div>
                      </div>
                    </div>
                    <div className="viaje-info-basica">
                      {viaje.valor && (
                        <div className="info-item valor">
                          <i className="fas fa-dollar-sign"></i>
                          <span>${parseFloat(viaje.valor).toFixed(2)}</span>
                        </div>
                      )}
                      {viaje.unidad && (
                        <div className="info-item unidad">
                          <i className="fas fa-taxi"></i>
                          <span>Unidad: {viaje.unidad}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Detalles expandibles */}
                  {expandedViajeId === viaje.id && (
                    <div className="viaje-detalles-expandidos">
                      {/* Client details section */}
                      <div className="client-details-section">
                        <div className="client-details-header">
                          <i className="fas fa-id-card"></i>
                          <span>Información del Cliente</span>
                        </div>
                        <div className="client-details-grid">
                          <div className="client-detail-item">
                            <div className="client-detail-label">Cliente</div>
                            <div className="client-detail-value">
                              <i className="fas fa-user"></i>
                              {viaje.nombreCliente || viaje.nombre || 'N/A'}
                            </div>
                          </div>
                          <div className="client-detail-item">
                            <div className="client-detail-label">CC Cliente</div>
                            <div className="client-detail-value">
                              <i className="fas fa-id-badge"></i>
                              {viaje.cc_cliente || 'N/A'}
                            </div>
                          </div>
                          <div className="client-detail-item">
                            <div className="client-detail-label">Cédula</div>
                            <div className="client-detail-value">
                              <i className="fas fa-passport"></i>
                              {viaje.aera || 'N/A'}
                            </div>
                          </div>
                          <div className="client-detail-item">
                            <div className="client-detail-label">Código Dirección</div>
                            <div className="client-detail-value">
                              <i className="fas fa-map-pin"></i>
                              {viaje.code_direccion || 'N/A'}
                            </div>
                          </div>
                          {viaje.departamento && (
                            <div className="client-detail-item">
                              <div className="client-detail-label">Departamento</div>
                              <div className="client-detail-value">
                                <i className="fas fa-building"></i>
                                {viaje.departamento}
                              </div>
                            </div>
                          )}
                          {viaje.telefono && (
                            <div className="client-detail-item">
                              <div className="client-detail-label">Teléfono</div>
                              <div className="client-detail-value">
                                <i className="fas fa-phone"></i>
                                {viaje.telefono}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Add Solicitante details section when available */}
                      {(hasSolicitanteInfo(viaje) || viaje.userEmail) && (
                        <div className="solicitante-details-section">
                          <div className="solicitante-details-header">
                            <i className="fas fa-user-tag"></i>
                            <span>Información del Solicitante</span>
                          </div>
                          <div className="client-details-grid">
                            {viaje.solicitante.nombre && (
                              <div className="client-detail-item">
                                <div className="client-detail-label">Nombre</div>
                                <div className="client-detail-value">
                                  <i className="fas fa-user"></i>
                                  {viaje.solicitante.nombre}
                                </div>
                              </div>
                            )}
                            <div className="client-detail-item">
                              <div className="client-detail-label">Email</div>
                              <div className="client-detail-value">
                                <i className="fas fa-envelope"></i>
                                {viaje.solicitante?.email || viaje.userEmail || 'N/A'}
                              </div>
                            </div>
                            {viaje.solicitante.cc_cliente && (
                              <div className="client-detail-item cc-cliente">
                                <div className="client-detail-label">CC Cliente</div>
                                <div className="client-detail-value">
                                  <i className="fas fa-id-badge"></i>
                                  {viaje.solicitante.cc_cliente}
                                </div>
                              </div>
                            )}
                            {viaje.solicitante.cedula && (
                              <div className="client-detail-item cedula">
                                <div className="client-detail-label">Cédula</div>
                                <div className="client-detail-value">
                                  <i className="fas fa-passport"></i>
                                  {viaje.solicitante.cedula}
                                </div>
                              </div>
                            )}
                            {viaje.solicitante.code_direccion && (
                              <div className="client-detail-item code-direccion">
                                <div className="client-detail-label">Código Dirección</div>
                                <div className="client-detail-value">
                                  <i className="fas fa-map-pin"></i>
                                  {viaje.solicitante.code_direccion}
                                </div>
                              </div>
                            )}
                            {viaje.solicitante.departamento && (
                              <div className="client-detail-item">
                                <div className="client-detail-label">Departamento</div>
                                <div className="client-detail-value">
                                  <i className="fas fa-building"></i>
                                  {viaje.solicitante.departamento}
                                </div>
                              </div>
                            )}
                            {viaje.solicitante.telefono && (
                              <div className="client-detail-item">
                                <div className="client-detail-label">Teléfono</div>
                                <div className="client-detail-value">
                                  <i className="fas fa-phone"></i>
                                  {viaje.solicitante.telefono}
                                </div>
                              </div>
                            )}
                            {viaje.solicitante.direccion && (
                              <div className="client-detail-item">
                                <div className="client-detail-label">Dirección</div>
                                <div className="client-detail-value">
                                  <i className="fas fa-home"></i>
                                  {viaje.solicitante.direccion}
                                </div>
                              </div>
                            )}
                          </div>
                          {viaje.solicitante.foto && (
                            <div className="solicitante-foto">
                              <img src={viaje.solicitante.foto} alt={viaje.solicitante.nombre || "Solicitante"} />
                            </div>
                          )}
                        </div>
                      )}
                      {/* Existing trip details */}
                      <div className="detalles-seccion">
                        <h4>Detalles del Viaje</h4>
                        <div className="viaje-detalles">
                          {/* Display driver comments field prominently */}
                          <div className="comentario-conductor-container">
                            <h5 className="comentario-conductor-title">
                              <i className="fas fa-comment-alt"></i> Comentarios del Conductor
                            </h5>
                            <div className="comentario-conductor-content">
                              {viaje.comentarioConductor ? (
                                <p>{viaje.comentarioConductor}</p>
                              ) : (
                                <p className="no-comentario">No hay comentarios del conductor</p>
                              )}
                            </div>
                          </div>

                          {/* Display department comments */}
                          <div className="comentario-departamento-container">
                            <h5 className="comentario-departamento-title">
                              <i className="fas fa-building"></i> Comentarios del Departamento
                            </h5>
                            <div className="comentario-departamento-content">
                              {viaje.departamentoComentario || viaje.comentarioDepartamento || viaje.comentario ? (
                                <p>{viaje.departamentoComentario || viaje.comentarioDepartamento || viaje.comentario}</p>
                              ) : (
                                <p className="no-comentario">No hay comentarios del departamento</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* For group trips, show passenger list */}
                      {isGroupTrip(viaje) && getPassengers(viaje).length > 0 && (
                        <div className="detalles-seccion">
                          <div className="pasajeros-list">
                            <div className="pasajeros-header">
                              <h4><i className="fas fa-users"></i> Pasajeros ({getPassengerCount(viaje)})</h4>
                            </div>
                            {getPassengers(viaje).map((pasajero, index) => (
                              <div className="pasajero-item" key={pasajero.id || index}>
                                <div className="pasajero-icon">
                                  {(pasajero.nombre || '?').charAt(0).toUpperCase()}
                                </div>
                                <div className="pasajero-info">
                                  <div className="pasajero-nombre">{pasajero.nombre || 'Sin nombre'}</div>
                                  <div className="pasajero-detalles">
                                    {pasajero.cc_cliente && (
                                      <div className="pasajero-detalle">
                                        <i className="fas fa-id-badge"></i> {pasajero.cc_cliente}
                                      </div>
                                    )}
                                    {pasajero.aera && (
                                      <div className="pasajero-detalle">
                                        <i className="fas fa-passport"></i> {pasajero.aera}
                                      </div>
                                    )}
                                    {pasajero.code_direccion && (
                                      <div className="pasajero-detalle">
                                        <i className="fas fa-map-pin"></i> {pasajero.code_direccion}
                                      </div>
                                    )}
                                    {pasajero.telefono && (
                                      <div className="pasajero-detalle">
                                        <i className="fas fa-phone"></i> {pasajero.telefono}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="viaje-footer">
                  <span className="viaje-id">
                    <i className="fas fa-fingerprint"></i> ID: {viaje.id ? viaje.id.substring(0, 8) + '...' : 'N/A'}
                  </span>
                  <button
                    className={`btn-ver-detalle ${expandedViajeId === viaje.id ? 'active' : ''}`}
                    onClick={() => toggleDetalles(viaje.id)}
                  >
                    {expandedViajeId === viaje.id ? (
                      <>
                        <i className="fas fa-chevron-up"></i> Ocultar Detalles
                      </>
                    ) : (
                      <>
                        <i className="fas fa-info-circle"></i> Ver Detalles
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add view limit controls section */}
        {!loading && !error && viajes.length > 0 && (
          <div className="view-limit-controls">
            <div className="view-limit-selector">
              <label htmlFor="maxViewItems">Máximo de registros a cargar:</label>
              <select
                id="maxViewItems"
                value={maxViewItems}
                onChange={(e) => {
                  setMaxViewItems(Number(e.target.value));
                  setCurrentPage(1); // Reset to first page when changing limit
                }}
              >
                <option value={50}>50 registros</option>
                <option value={100}>100 registros</option>
                <option value={200}>200 registros</option>
                <option value={500}>500 registros</option>
                <option value={1000}>1000 registros</option>
              </select>
            </div>

            {viajes.length > maxViewItems && (
              <div className="view-limit-warning">
                <i className="fas fa-exclamation-triangle"></i>
                Mostrando {maxViewItems} de {viajes.length} viajes. Puede aumentar el límite o aplicar filtros para reducir los resultados.
              </div>
            )}
          </div>
        )}

        {/* Add Passengers Modal */}
        {passengerModalOpen && selectedPassengers && (
          <div className="grupo-details-overlay" onClick={closePassengersModal}>
            <div
              className="grupo-details-modal passenger-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="grupo-modal-header">
                <h3>
                  <i className="fas fa-users"></i> Detalle de Pasajeros
                </h3>
                <button
                  className="close-modal-btn"
                  onClick={closePassengersModal}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="grupo-modal-content">
                {renderGroupTripDetails({
                  ...selectedTripData,
                  pasajeros: selectedPassengers
                })}
              </div>
            </div>
          </div>
        )}

        {/* Edit Trip Modal */}
        {HISTORY_ACTIONS_ENABLED && editModalOpen && (
          <div className="grupo-details-overlay" onClick={closeEditModal}>
            <div
              className="grupo-details-modal edit-trip-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="grupo-modal-header">
                <h3>
                  <i className="fas fa-edit"></i> Editar Detalles del Viaje
                </h3>
                <button
                  className="close-modal-btn"
                  onClick={closeEditModal}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="grupo-modal-content edit-modal-body">
                <div className="edit-form-grid">
                  <div className="edit-form-group">
                    <label>Hora Inicio</label>
                    <input
                      type="text"
                      name="hora"
                      value={editFormData.hora}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="edit-form-group">
                    <label>Usuario</label>
                    <input
                      type="text"
                      name="nombreCliente"
                      value={editFormData.nombreCliente}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="edit-form-group">
                    <label>CC Cliente (C. Depart)</label>
                    <input
                      type="text"
                      name="cc_cliente"
                      value={editFormData.cc_cliente}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="edit-form-group">
                    <label>C.I / Cédula</label>
                    <input
                      type="text"
                      name="cedula"
                      value={editFormData.cedula}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="edit-form-group">
                    <label>Departamento</label>
                    <input
                      type="text"
                      name="departamento"
                      value={editFormData.departamento}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="edit-form-group">
                    <label>Origen</label>
                    <input
                      type="text"
                      name="direccion"
                      value={editFormData.direccion}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="edit-form-group">
                    <label>Destino</label>
                    <input
                      type="text"
                      name="destino"
                      value={editFormData.destino}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="edit-form-group">
                    <label>Hora Final</label>
                    <input
                      type="text"
                      name="tiempoFinalTaximetro"
                      value={editFormData.tiempoFinalTaximetro}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="edit-form-group">
                    <label>Valor</label>
                    <input
                      type="number"
                      step="0.01"
                      name="valor"
                      value={editFormData.valor}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="edit-form-group">
                    <label>Unidad</label>
                    <input
                      type="text"
                      name="unidad"
                      value={editFormData.unidad}
                      onChange={handleEditChange}
                    />
                  </div>
                </div>
              </div>
              <div className="grupo-modal-footer">
                <button
                  className="btn-cancelar"
                  onClick={closeEditModal}
                  disabled={isSaving}
                >
                  Cancelar
                </button>
                <button
                  className="btn-guardar"
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                >
                  {isSaving ? <><i className="fas fa-spinner fa-spin"></i> Guardando...</> : 'Guardar Cambios'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HistorialViajes;
